import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor,
  useSensor, useSensors, DragEndEvent, DragStartEvent,
  useDroppable, useDraggable, rectIntersection,
} from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import {
  Kanban, Plus, AlertTriangle, Loader2, User, Filter,
  Settings2, Mail, MessageSquare, Heart, Star, AlertCircle,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import {
  usePipeline, PipelineStatus, PipelineClient,
  PIPELINE_COLUMNS, PIPELINE_STATUSES,
} from '../hooks/usePipeline';
import { useStudio } from '@/features/studio/contexts/StudioContext';
import { useStudioMembers } from '@/features/studio/hooks/useStudio';
import { ClientFormDialog } from '@/features/clients/components/ClientFormDialog';
import { useClients } from '@/features/clients/hooks/useClients';
import type { NewClientData } from '@/features/clients/components/ClientFormDialog';

// ─── Constants ────────────────────────────────────────────────────────────────

const AUTOMATIONS: Record<string, string> = {
  'lead→consultation':          'Aucune automatisation',
  'consultation→consent_sent':  'Email consentement envoyé au client',
  'consent_sent→confirmed':     'SMS confirmation RDV envoyé',
  'confirmed→in_session':       'Checklist pré-session déclenchée',
  'in_session→retouch':         'Email soins post-acte envoyé',
  'retouch→completed':          "Demande d'avis Google envoyée",
};

const AUTOMATION_PILLS = [
  { icon: Mail,          label: 'Email consentement' },
  { icon: MessageSquare, label: 'SMS confirmation RDV' },
  { icon: Heart,         label: 'Email soins post-acte' },
  { icon: Star,          label: "Demande d'avis" },
];

const STATUS_LABEL: Record<PipelineStatus, string> = {
  lead:         'Leads',
  consultation: 'Consultation',
  consent_sent: 'Consentement envoyé',
  confirmed:    'RDV confirmé',
  in_session:   'Session en cours',
  retouch:      'Retouche',
  completed:    'Terminé',
  lost:         'Perdu',
};

// Exact colors from spec — inline styles used for precision
const COLUMN_STYLE: Record<PipelineStatus, { bg: string; color: string }> = {
  lead:         { bg: '#E6F1FB', color: '#0C447C' },
  consultation: { bg: '#EEEDFE', color: '#534AB7' },
  consent_sent: { bg: '#FAEEDA', color: '#633806' },
  confirmed:    { bg: '#E1F5EE', color: '#085041' },
  in_session:   { bg: '#1D9E75', color: '#fff' },
  retouch:      { bg: '#9FE1CB', color: '#04342C' },
  completed:    { bg: 'var(--color-background-secondary, #f3f4f6)', color: 'var(--color-text-secondary, #6b7280)' },
  lost:         { bg: '#fee2e2', color: '#7f1d1d' },
};

// Border color for drop zone / add button (matches theme)
const COLUMN_BORDER: Record<PipelineStatus, string> = {
  lead:         'border-sky-200/60',
  consultation: 'border-violet-200/60',
  consent_sent: 'border-amber-200/60',
  confirmed:    'border-emerald-200/60',
  in_session:   'border-green-300/40',
  retouch:      'border-teal-300/40',
  completed:    'border-border/40',
  lost:         'border-red-200/60',
};

const AVATAR_PALETTE: [string, string][] = [
  ['#ef4444', '#fff'], ['#f97316', '#fff'], ['#f59e0b', '#fff'],
  ['#10b981', '#fff'], ['#14b8a6', '#fff'], ['#3b82f6', '#fff'],
  ['#8b5cf6', '#fff'], ['#ec4899', '#fff'],
];

function avatarStyle(client: PipelineClient): { background: string; color: string } {
  const code = (client.first_name.charCodeAt(0) || 65) + (client.last_name.charCodeAt(0) || 65);
  const [bg, fg] = AVATAR_PALETTE[code % AVATAR_PALETTE.length];
  return { background: bg, color: fg };
}

function initials(client: PipelineClient) {
  return `${(client.first_name[0] || '').toUpperCase()}${(client.last_name[0] || '').toUpperCase()}` || '?';
}

// ─── Profiles query (display_name by owner_id) ────────────────────────────────

function useOwnerProfiles(ownerIds: string[]) {
  return useQuery({
    queryKey: ['profiles', ownerIds.slice().sort()],
    enabled: ownerIds.length > 0,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await (supabase as any)
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', ownerIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data || []).forEach((p: any) => { if (p.display_name) map[p.user_id] = p.display_name; });
      return map;
    },
  });
}

// ─── Confirm-move Dialog ──────────────────────────────────────────────────────

interface MoveDialogProps {
  client: PipelineClient | null;
  fromStatus: PipelineStatus | null;
  toStatus: PipelineStatus | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function MoveDialog({ client, fromStatus, toStatus, onConfirm, onCancel }: MoveDialogProps) {
  const open = !!client && !!fromStatus && !!toStatus;
  const key = fromStatus && toStatus ? `${fromStatus}→${toStatus}` : '';
  const automation = AUTOMATIONS[key] || 'Aucune automatisation';
  const hasAuto = automation !== 'Aucune automatisation';

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Déplacer le client</DialogTitle>
        </DialogHeader>
        {client && fromStatus && toStatus && (
          <div className="space-y-4 py-1">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/40">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={avatarStyle(client)}
              >
                {initials(client)}
              </div>
              <div>
                <p className="text-sm font-semibold">{client.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium"
                    style={{ background: COLUMN_STYLE[fromStatus].bg, color: COLUMN_STYLE[fromStatus].color }}
                  >
                    {STATUS_LABEL[fromStatus]}
                  </span>
                  <ChevronRight size={11} className="text-muted-foreground" />
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium"
                    style={{ background: COLUMN_STYLE[toStatus].bg, color: COLUMN_STYLE[toStatus].color }}
                  >
                    {STATUS_LABEL[toStatus]}
                  </span>
                </div>
              </div>
            </div>
            <div className={`flex items-start gap-2.5 p-3 rounded-xl border ${hasAuto ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-border/50'}`}>
              {hasAuto
                ? <Mail size={13} className="text-primary mt-0.5 shrink-0" />
                : <AlertCircle size={13} className="text-muted-foreground mt-0.5 shrink-0" />
              }
              <p className="text-xs text-muted-foreground leading-relaxed">{automation}</p>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <button onClick={onCancel} className="flex-1 px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-secondary transition-colors">
            Annuler
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
            Confirmer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

interface KanbanCardProps {
  client: PipelineClient;
  artistName?: string;
  overlay?: boolean;
  onClick?: () => void;
}

function KanbanCard({ client, artistName, overlay = false, onClick }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: client.id });

  return (
    <motion.div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: isDragging && !overlay ? 0 : 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.15 }}
      onClick={e => { if (!isDragging && onClick) { e.stopPropagation(); onClick(); } }}
      className={[
        'group relative p-3 rounded-xl border bg-card select-none transition-all duration-150',
        'cursor-grab active:cursor-grabbing',
        overlay
          ? 'shadow-2xl ring-2 ring-primary/30 rotate-1 scale-105'
          : 'border-border/50 hover:border-primary/30 hover:shadow-md hover:shadow-black/5',
      ].join(' ')}
    >
      <div className="flex items-start gap-2.5">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
          style={avatarStyle(client)}
        >
          {initials(client)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold leading-tight truncate text-foreground">{client.name}</p>
          {client.style_preferences.length > 0 && (
            <p className="mt-0.5 text-[11px] text-muted-foreground truncate">{client.style_preferences[0]}</p>
          )}
        </div>
        {client.has_allergy && (
          <span title="Allergie signalée" className="shrink-0 mt-0.5">
            <AlertTriangle size={11} className="text-red-500" />
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 mt-2 pl-[calc(1.75rem+0.625rem)]">
        <User size={10} className="text-muted-foreground/40 shrink-0" />
        <span className="text-[10px] text-muted-foreground/50 truncate">
          {artistName || '—'}
        </span>
      </div>
    </motion.div>
  );
}

// ─── Column ───────────────────────────────────────────────────────────────────

interface KanbanColumnProps {
  id: PipelineStatus;
  clients: PipelineClient[];
  artistNames: Record<string, string>;
  onAdd: () => void;
  onCardClick: (id: string) => void;
}

function KanbanColumn({ id, clients, artistNames, onAdd, onCardClick }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const { bg, color } = COLUMN_STYLE[id];
  const borderClass = COLUMN_BORDER[id];

  return (
    <div
      ref={setNodeRef}
      className={[
        'flex flex-col rounded-2xl border transition-all duration-200 min-w-[160px] flex-1 overflow-hidden',
        isOver
          ? 'border-primary/40 shadow-lg shadow-primary/10 scale-[1.01]'
          : 'border-border/40 bg-card/50',
      ].join(' ')}
      style={{ maxWidth: 240 }}
    >
      {/* Header — colored block */}
      <div
        className="flex items-center gap-2 px-3 py-2 shrink-0"
        style={{ background: bg }}
      >
        <span className="flex-1 text-xs font-bold truncate" style={{ color }}>
          {STATUS_LABEL[id]}
        </span>
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
          style={{ background: `color-mix(in srgb, ${bg} 60%, transparent)`, color }}
        >
          {clients.length}
        </span>
      </div>

      {/* Drop area + cards + add button */}
      <div
        className={[
          'flex-1 overflow-y-auto px-2 py-1.5 space-y-1.5 min-h-[60px] transition-colors',
          isOver ? 'bg-primary/5' : '',
        ].join(' ')}
      >
        <AnimatePresence>
          {clients.map(c => (
            <KanbanCard
              key={c.id}
              client={c}
              artistName={artistNames[c.owner_id]}
              onClick={() => onCardClick(c.id)}
            />
          ))}
        </AnimatePresence>

        {/* + Ajouter — inside drop area, after last card */}
        <button
          onClick={onAdd}
          className={[
            'w-full flex items-center justify-center gap-1 py-1.5 rounded-xl border border-dashed',
            'text-[11px] font-medium text-muted-foreground/60 hover:text-primary hover:border-primary/40 transition-colors',
            borderClass,
          ].join(' ')}
        >
          <Plus size={10} /> Ajouter
        </button>

        {clients.length === 0 && !isOver && (
          <div className="flex items-center justify-center h-6 text-[10px] text-muted-foreground/25 select-none">
            Déposer ici
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Pipeline() {
  const navigate = useNavigate();
  const { clients, columns, isLoading, updatePipelineStatus } = usePipeline();
  const { membership } = useStudio();
  const { data: studioMembers = [] } = useStudioMembers();
  const { createClient, updateClient } = useClients();

  const [activeId, setActiveId]         = useState<string | null>(null);
  const [filterArtist, setFilterArtist] = useState<string>('all');
  const [showFilter, setShowFilter]     = useState(false);

  const [pendingMove, setPendingMove] = useState<{
    clientId: string; from: PipelineStatus; to: PipelineStatus;
  } | null>(null);

  const [addDialog, setAddDialog] = useState<{ open: boolean; status: PipelineStatus | null }>({
    open: false, status: null,
  });

  // Fetch display_names for all unique owner_ids
  const ownerIds = [...new Set(clients.map(c => c.owner_id).filter(Boolean))];
  const { data: artistNames = {} } = useOwnerProfiles(ownerIds);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const activeClient = activeId ? (clients.find(c => c.id === activeId) ?? null) : null;

  const handleDragStart = (e: DragStartEvent) => setActiveId(e.active.id as string);

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    setActiveId(null);
    if (!over) return;
    const newStatus = over.id as PipelineStatus;
    if (!PIPELINE_STATUSES.includes(newStatus)) return;
    const current = clients.find(c => c.id === active.id)?.pipeline_status;
    if (current && newStatus !== current) {
      setPendingMove({ clientId: active.id as string, from: current, to: newStatus });
    }
  };

  const confirmMove = () => {
    if (!pendingMove) return;
    updatePipelineStatus(pendingMove.clientId, pendingMove.to);
    toast.success(`Statut mis à jour → ${STATUS_LABEL[pendingMove.to]}`);
    setPendingMove(null);
  };

  const activeMembers = studioMembers.filter(m => m.status === 'active');
  const isStudioOwner = !!membership?.isOwner;

  const filteredColumns = PIPELINE_STATUSES.reduce((acc, s) => {
    const col = columns[s] || [];
    acc[s] = filterArtist === 'all' ? col : col.filter(c => c.owner_id === filterArtist);
    return acc;
  }, {} as Record<PipelineStatus, PipelineClient[]>);

  const totalActive = clients.filter(c => c.pipeline_status !== 'lost').length;

  const handleCreateClient = async (data: NewClientData) => {
    const created = await createClient.mutateAsync(data as any);
    if (created && addDialog.status && addDialog.status !== 'lead') {
      updatePipelineStatus(created.id, addDialog.status);
    }
    toast.success(`${data.first_name} ${data.last_name} ajouté`);
    setAddDialog({ open: false, status: null });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={22} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background">

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border/50 shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <Kanban size={18} className="text-primary shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm font-bold text-foreground leading-tight">Pipeline clients</h1>
            <p className="text-[11px] text-muted-foreground">
              {totalActive} client{totalActive !== 1 ? 's' : ''} · Drag &amp; drop pour changer le statut
            </p>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 shrink-0">
          {isStudioOwner && (
            <div className="relative">
              <button
                onClick={() => setShowFilter(p => !p)}
                className={[
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors',
                  showFilter
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'border-border hover:bg-secondary text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                <Filter size={12} /> Filtrer
              </button>
              <AnimatePresence>
                {showFilter && (
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.97 }}
                    transition={{ duration: 0.12 }}
                    className="absolute right-0 top-full mt-1.5 z-20 bg-card border border-border rounded-xl shadow-xl p-1.5 min-w-[180px]"
                  >
                    <button
                      onClick={() => { setFilterArtist('all'); setShowFilter(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${filterArtist === 'all' ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-secondary text-muted-foreground'}`}
                    >
                      Tous les artistes
                    </button>
                    {activeMembers.map(m => (
                      <button
                        key={m.user_id}
                        onClick={() => { setFilterArtist(m.user_id); setShowFilter(false); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${filterArtist === m.user_id ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-secondary text-muted-foreground'}`}
                      >
                        {m.display_name || m.email || '—'}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            <Settings2 size={12} /> Configurer
          </button>
        </div>
      </div>

      {/* Kanban */}
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-2 p-3 h-full" style={{ minWidth: 'max-content' }}>
            {PIPELINE_COLUMNS.map((col, i) => (
              <motion.div
                key={col.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.2 }}
                className="h-full flex flex-col"
                style={{ minWidth: 160, flex: 1, maxWidth: 240 }}
              >
                <KanbanColumn
                  id={col.id}
                  clients={filteredColumns[col.id] || []}
                  artistNames={artistNames}
                  onAdd={() => setAddDialog({ open: true, status: col.id })}
                  onCardClick={id => navigate(`/clients/${id}`)}
                />
              </motion.div>
            ))}
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeClient && (
            <KanbanCard
              client={activeClient}
              artistName={artistNames[activeClient.owner_id]}
              overlay
            />
          )}
        </DragOverlay>
      </DndContext>

      {/* Footer automations */}
      <div className="shrink-0 border-t border-border/40 bg-card/60 px-5 py-2.5 flex items-center gap-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground shrink-0">
          Automatisations actives
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {AUTOMATION_PILLS.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary/5 border border-primary/15 text-[10px] font-medium text-primary/80"
            >
              <Icon size={9} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Move dialog */}
      <MoveDialog
        client={pendingMove ? (clients.find(c => c.id === pendingMove.clientId) ?? null) : null}
        fromStatus={pendingMove?.from ?? null}
        toStatus={pendingMove?.to ?? null}
        onConfirm={confirmMove}
        onCancel={() => setPendingMove(null)}
      />

      {/* Add client — ClientFormDialog with pipeline_status preset */}
      <ClientFormDialog
        open={addDialog.open}
        onOpenChange={open => { if (!open) setAddDialog({ open: false, status: null }); }}
        editing={null}
        onCreateClient={handleCreateClient}
        onUpdateClient={async (id, data) => { await updateClient.mutateAsync({ id, ...data }); }}
      />
    </div>
  );
}
